import is from '@sindresorhus/is';
import clsx from 'clsx';
import noop from 'lodash/noop';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, useMemo, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconChevronRightSmall } from '~/ui/core/icons';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import {
    TableBody,
    TableCell,
    type TableColumnWidth,
    TableContainer,
    TableFullWidthRow,
    TableGrid,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '~/ui/core/table/Table';
import { Tile, tileContentEdgeOffsetClassNames } from '~/ui/core/tile/Tile';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';

import { getProficiencyScaleCount, useSkillProficiencyLevelName } from '../../app/common/skills';
import { stripCommentMarks } from '../../comments';
import { SectionContent } from '../../comments/section-content';
import { useOptionalTileAvailability, useOptionalUrlTiles } from '../../url-tiles';
import { ProficiencyLevelDropdownMenu } from '../job-profile-section/skills/ProficiencyLevelDropdownMenu';
import { useTranslatedSectionTitle } from '../job-profile-section/use-translated-section-title';
import { profileTaskTranslationKeys } from '../profile-tasks/profile-task-translations';
import { JSA_PROFILE_TASK_DETAIL_TILE_TYPE } from '../profile-tasks/tile/profile-task-tile-types';
import { createPersistedProfileTaskTilePayload } from '../profile-tasks/tile/task-tile-url';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import {
    buildProfileSectionComparison,
    buildProfileTaskComparison,
    buildSkillComparison,
    type ComparedProfileDetails,
    hasStructuredTasks,
    type ProfileSectionComparisonCell,
    type ProfileSectionComparisonRow,
    type ProfileTaskComparisonColumn,
    type ProfileTaskComparisonEntry,
    type SkillComparisonCell,
    type SkillComparisonRow,
} from './build-profile-comparison';
import { getComparisonContentState } from './comparison-content-state';
import type { ComparisonProficiencySettings } from './use-comparison-proficiency-settings';

const MIN_COLUMN_WIDTH = 200;
const PROFICIENCY_HIGHLIGHT_CLASSES = [
    'bg-orange-50',
    'bg-orange-100',
    'bg-orange-200',
    'bg-orange-300',
    'bg-orange-400',
    'bg-orange-500',
    'bg-orange-600',
] as const;
const COMPARISON_LOADING_ROW_IDS = ['first', 'second', 'third'] as const;

// One column of a comparison: the profile shown, plus the loading state of its detail and version queries.
export type ComparisonColumn = {
    readonly details: ComparedProfileDetails | undefined;
    readonly error: Error | null;
    readonly id: string;
    readonly isFetching: boolean;
    readonly isInitialLoading: boolean;
    readonly versions: JobProfileVersions | undefined;
};

const getProficiencyHighlightClass = (level: number | null | undefined): string => {
    const normalizedLevel = Math.min(Math.max(level ?? 0, 0), PROFICIENCY_HIGHLIGHT_CLASSES.length - 1);

    return PROFICIENCY_HIGHLIGHT_CLASSES[normalizedLevel] ?? PROFICIENCY_HIGHLIGHT_CLASSES[0];
};

const getSkillCellClassName = (highlightProficiency: boolean, cell: SkillComparisonCell): string =>
    clsx(
        'min-w-0',
        highlightProficiency && cell.isRequired ? getProficiencyHighlightClass(cell.proficiency) : undefined,
    );

export const ComparisonStaticHeader: FunctionComponent<{ readonly onClose: () => void }> = ({ onClose }) => (
    <div className={tileContentEdgeOffsetClassNames}>
        <SidepanelHeader
            activeLanguage={undefined}
            disableArrows={true}
            isGenerating={false}
            isLanguagePickerDisabled={true}
            languages={[]}
            setActiveLanguage={noop}
            onClose={onClose}
        />
    </div>
);

export const ComparisonLoadingTile: FunctionComponent<{ readonly onClose: () => void; readonly testId: string }> = ({
    onClose,
    testId,
}) => (
    <Tile className="gap-0" data-testid={testId}>
        <div className="flex w-full flex-col gap-4xl px-6xl pb-xxl pt-tile-edge">
            <ComparisonStaticHeader onClose={onClose} />
            <SkeletonBase className="h-3xl w-[18rem]" />
            <SkeletonBase className="h-[20rem] w-full rounded-xl" />
        </div>
    </Tile>
);

const ComparisonOptionsMenu: FunctionComponent<ComparisonProficiencySettings> = ({
    highlightProficiency,
    onHighlightProficiencyChange,
    onShowProficiencyChange,
    showProficiency,
}) => {
    const translate = useTranslate();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button size="sm" variant="secondary">
                    {translate('common.options')}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={5}>
                <DropdownMenuCheckboxItem
                    checked={showProficiency}
                    onSelect={(event) => {
                        event.preventDefault();
                        onShowProficiencyChange(!showProficiency);
                    }}
                >
                    {translate('overwrites.skill.level')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                    checked={highlightProficiency}
                    onSelect={(event) => {
                        event.preventDefault();
                        onHighlightProficiencyChange(!highlightProficiency);
                    }}
                >
                    {translate('pages.job-architecture-v2.highlight-proficiency')}
                </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export const ComparisonHeader: FunctionComponent<{
    /** Null hides the proficiency options, e.g. when the job profile template hides role proficiency. */
    readonly proficiencySettings: ComparisonProficiencySettings | null;
    readonly onClose: () => void;
    readonly onLanguageChange: (language: LanguageCodeForJSA) => void;
}> = ({ onClose, onLanguageChange, proficiencySettings }) => {
    const translate = useTranslate();
    const { activeLanguage, availableLanguages } = useActiveLanguage();

    return (
        <div className={tileContentEdgeOffsetClassNames}>
            <SidepanelHeader
                activeLanguage={translate(`common.languages.${activeLanguage}` as TranslationKey)}
                disableArrows={true}
                isGenerating={false}
                isLanguagePickerDisabled={false}
                languages={availableLanguages}
                setActiveLanguage={onLanguageChange}
                actions={is.null(proficiencySettings) ? undefined : <ComparisonOptionsMenu {...proficiencySettings} />}
                onClose={onClose}
            />
        </div>
    );
};

const ProficiencyCell: FunctionComponent<{
    readonly cell: SkillComparisonCell;
    readonly skillId: string;
    readonly skillType: 'hard' | 'language' | 'soft';
}> = ({ cell, skillId, skillType }) => {
    const translate = useTranslate();
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();

    const proficiency = cell.proficiency ?? 0;
    const proficiencyName =
        getProficiencyLevelName({ level: proficiency, skillType }) ||
        translate('pages.job-architecture-v2.job-profile-no-skill-level');

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <button
                    aria-label={proficiencyName}
                    className="flex min-h-[3rem] shrink-0 items-center justify-end rounded-md border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring"
                    title={proficiencyName}
                    type="button"
                >
                    <ProficiencyScale
                        active={true}
                        level={proficiency}
                        scaleCount={getProficiencyScaleCount(skillType)}
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="center"
                collisionPadding={16}
                side="right"
                sideOffset={8}
                style={{ overflow: 'hidden', zIndex: 2000 }}
            >
                <ProficiencyLevelDropdownMenu
                    activeLevel={proficiency}
                    disabled={true}
                    id={skillId}
                    onChangeProficiencyLevel={noop}
                />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const ProfileSectionCell: FunctionComponent<{ readonly cell: ProfileSectionComparisonCell }> = ({ cell }) => {
    const innerHtml = useMemo(
        () => ({ __html: is.nonEmptyString(cell.value) ? stripCommentMarks(cell.value) : '' }),
        [cell.value],
    );

    if (!is.nonEmptyString(cell.value)) {
        return <span className="text-text-quiet">—</span>;
    }

    return (
        <SectionContent
            className="text-body-xxs text-text-muted [&_ol]:pl-l [&_ul]:pl-l"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: job profile sections are stored as rich HTML and rendered the same way as profile sections.
            dangerouslySetInnerHTML={innerHtml}
        />
    );
};

const ComparisonGroupHeader: FunctionComponent<{
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
    readonly isCollapsed?: boolean;
    readonly hasTopMargin: boolean;
    readonly onToggle?: () => void;
    readonly title: string;
}> = ({ columnWidths, hasTopMargin, isCollapsed = false, onToggle, title }) => {
    const headerContent =
        onToggle === undefined ? (
            title
        ) : (
            <button
                aria-expanded={!isCollapsed}
                className="flex w-full items-center justify-between gap-s border-none bg-transparent p-0 text-left font-semibold text-text-primary"
                type="button"
                onClick={onToggle}
            >
                <span>{title}</span>
                <IconChevronRightSmall
                    aria-hidden={true}
                    className="shrink-0 transition-transform duration-200 ease-in-out"
                    size={16}
                    style={{ transform: isCollapsed ? undefined : 'rotate(90deg)' }}
                />
            </button>
        );

    return (
        <TableFullWidthRow
            $autoHeight={true}
            $columnWidths={columnWidths}
            className={hasTopMargin ? '!mt-m' : '!mt-0'}
            cellClassName="!min-h-0 !px-l !pb-xs !pt-l text-body-s font-medium text-text-primary"
            role="row"
        >
            {headerContent}
        </TableFullWidthRow>
    );
};

const UnavailableCell: FunctionComponent<{ readonly column: ComparisonColumn }> = ({ column }) =>
    column.isInitialLoading ? (
        <SkeletonBase className="h-l w-full" />
    ) : (
        <span className="text-text-quiet" title={column.error?.message}>
            —
        </span>
    );

const ComparisonLoadingRows: FunctionComponent<{
    readonly columns: readonly ComparisonColumn[];
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
    readonly label: string;
    readonly trailingCell: ReactNode;
}> = ({ columns, columnWidths, label, trailingCell }) =>
    COMPARISON_LOADING_ROW_IDS.map((rowId) => (
        <TableRow key={rowId} $animateLayout={false} $columnWidths={columnWidths} aria-label={label} role="row">
            {columns.map((column) => (
                <TableCell key={column.id} aria-busy={column.isInitialLoading} role="gridcell">
                    <UnavailableCell column={column} />
                </TableCell>
            ))}
            {trailingCell}
        </TableRow>
    ));

const ProfileComparisonCell: FunctionComponent<{
    readonly column: ComparisonColumn;
    readonly row: ProfileSectionComparisonRow;
    readonly sectionTitle: string;
}> = ({ column, row, sectionTitle }) => {
    const cell = row.cells.find(({ itemId }) => itemId === column.id) ?? { itemId: column.id, value: undefined };

    return (
        <TableCell
            className={clsx('items-start', column.isFetching && 'opacity-60')}
            aria-busy={column.isFetching || column.isInitialLoading}
            role="gridcell"
        >
            {is.undefined(column.details) ? (
                <UnavailableCell column={column} />
            ) : (
                <div className="flex min-w-0 flex-col gap-xs">
                    <span className="font-medium text-text-primary text-wrap-pretty">{sectionTitle}</span>
                    <ProfileSectionCell cell={cell} />
                </div>
            )}
        </TableCell>
    );
};

// Opens the task tile for a compared task, pinned to the version the column shows. Returns undefined
// when tasks are not interactive here: flag off, no tile host to open into, or a route that does not
// declare the task tile (the standalone profile page), where openTile would silently drop the payload.
const useOpenComparedTask = ():
    | ((details: ComparedProfileDetails, task: ProfileTaskComparisonEntry) => void)
    | undefined => {
    const { applicationState } = useApplicationContext();
    const { activeLanguage } = useActiveLanguage();
    const urlTiles = useOptionalUrlTiles();
    const tileAvailability = useOptionalTileAvailability();
    const isEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('structured-profile-tasks');
    const isTaskTileAvailable =
        tileAvailability === null || tileAvailability.availabilityByType.has(JSA_PROFILE_TASK_DETAIL_TILE_TYPE);

    if (!isEnabled || urlTiles === null || !isTaskTileAvailable) {
        return undefined;
    }

    return (details, task) => {
        const parentVersion = details.versionInfo?.version;

        if (is.undefined(parentVersion)) {
            return;
        }

        urlTiles.openTile(
            createPersistedProfileTaskTilePayload({
                architectureId: details.architectureId,
                language: activeLanguage,
                parentId: details.id,
                parentType: details.type === 'job-role' ? 'role' : 'level',
                parentVersion,
                roleId: details.type === 'job-level' ? details.parentId : undefined,
                taskId: task.id,
                taskVersionId: task.taskVersionId,
            }),
        );
    };
};

// Matches the open task tile by task, task version, and parent version, so switching a column to
// another version of the same profile does not keep the old row underlined.
const useIsTaskTileOpen = (): ((details: ComparedProfileDetails, task: ProfileTaskComparisonEntry) => boolean) => {
    const urlTiles = useOptionalUrlTiles();

    return (details, task) =>
        urlTiles?.tiles.some(
            ({ payload }) =>
                payload.type === JSA_PROFILE_TASK_DETAIL_TILE_TYPE &&
                payload['taskId'] === task.id &&
                payload['taskVersionId'] === task.taskVersionId &&
                payload['parentId'] === details.id &&
                payload['parentVersion'] === details.versionInfo?.version,
        ) ?? false;
};

const TaskComparisonCell: FunctionComponent<{
    readonly column: ComparisonColumn;
    readonly taskColumn: ProfileTaskComparisonColumn | undefined;
    readonly onOpenTask: ((details: ComparedProfileDetails, task: ProfileTaskComparisonEntry) => void) | undefined;
}> = ({ column, onOpenTask, taskColumn }) => {
    const isTaskTileOpen = useIsTaskTileOpen();
    const details = column.details;

    return (
        <TableCell
            className={clsx('items-start', column.isFetching && 'opacity-60')}
            aria-busy={column.isFetching || column.isInitialLoading}
            role="gridcell"
        >
            {is.undefined(details) ? (
                <UnavailableCell column={column} />
            ) : is.undefined(taskColumn) || is.emptyArray(taskColumn.tasks) ? (
                <span className="text-text-quiet">—</span>
            ) : (
                <ul className="m-0 flex min-w-0 list-none flex-col gap-s p-0">
                    {taskColumn.tasks.map((task) => (
                        <li key={task.id} className="flex min-w-0 flex-col gap-xxs" data-testid="comparison-task">
                            {is.undefined(onOpenTask) ? (
                                <span className="font-medium text-text-primary text-wrap-pretty">{task.title}</span>
                            ) : (
                                <button
                                    className={clsx(
                                        'min-w-0 cursor-pointer rounded-md border-none bg-transparent p-0 text-left font-sans text-body-s font-medium leading-normal text-text-primary underline-offset-2 [overflow-wrap:anywhere] hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
                                        isTaskTileOpen(details, task) && 'underline',
                                    )}
                                    type="button"
                                    onClick={() => onOpenTask(details, task)}
                                >
                                    {task.title}
                                </button>
                            )}
                            {is.nonEmptyString(task.description) ? (
                                <span className="text-body-xxs text-text-muted text-wrap-pretty">
                                    {task.description}
                                </span>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}
        </TableCell>
    );
};

const SkillComparisonCellView: FunctionComponent<{
    readonly column: ComparisonColumn;
    readonly highlightProficiency: boolean;
    readonly row: SkillComparisonRow;
    readonly showProficiency: boolean;
    readonly onOpenSkill: (skillId: string) => void;
}> = ({ column, highlightProficiency, onOpenSkill, row, showProficiency }) => {
    const translate = useTranslate();
    const cell = row.cells.find(({ itemId }) => itemId === column.id) ?? {
        isRequired: false,
        itemId: column.id,
        proficiency: undefined,
    };

    return (
        <TableCell
            className={clsx(getSkillCellClassName(highlightProficiency, cell), column.isFetching && 'opacity-60')}
            aria-busy={column.isFetching || column.isInitialLoading}
            aria-label={
                is.undefined(column.details) || cell.isRequired
                    ? undefined
                    : translate('pages.job-architecture-v2.job-profile-level-not-required')
            }
            role="gridcell"
        >
            {is.undefined(column.details) ? (
                <UnavailableCell column={column} />
            ) : cell.isRequired ? (
                <div className="flex min-w-0 w-full items-center justify-between gap-s">
                    <SkillBadge
                        className="min-w-0 max-w-full"
                        skill={{ title: row.title, type: row.skillType }}
                        onClick={() => onOpenSkill(row.id)}
                    />
                    {showProficiency ? (
                        <ProficiencyCell cell={cell} skillId={row.id} skillType={row.skillType} />
                    ) : null}
                </div>
            ) : null}
        </TableCell>
    );
};

const startsNewCompetency = (row: SkillComparisonRow, previousRow: SkillComparisonRow | undefined): boolean =>
    !is.undefined(row.competencyId) &&
    !is.undefined(row.competencyTitle) &&
    row.competencyId !== previousRow?.competencyId;

export const ProfileComparisonTable: FunctionComponent<{
    readonly columns: readonly ComparisonColumn[];
    readonly emptyMessage: string;
    readonly label: string;
    readonly proficiencySettings: ComparisonProficiencySettings;
    readonly renderHeaderCell: (column: ComparisonColumn) => ReactNode;
    // An extra fixed-width column after the compared profiles, used for the add-role control.
    readonly trailingColumn?: { readonly header: ReactNode; readonly width: TableColumnWidth };
    readonly onOpenSkill: (skillId: string) => void;
}> = ({ columns, emptyMessage, label, onOpenSkill, proficiencySettings, renderHeaderCell, trailingColumn }) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const { highlightProficiency, showProficiency } = proficiencySettings;
    const items = useMemo(() => columns.flatMap(({ details }) => (is.undefined(details) ? [] : [details])), [columns]);
    const resolveSectionTitle = useTranslatedSectionTitle(
        useMemo(() => items.flatMap((item) => item.jobProfile?.sectionTags ?? []), [items]),
    );
    // Legacy profiles keep their tasks as a plain section; once structured tasks exist the dedicated
    // group takes over and the synthesized task elements leave the section rows.
    const showTasks = useMemo(() => hasStructuredTasks(items), [items]);
    const sectionRows = useMemo(
        () => buildProfileSectionComparison(items, activeLanguage, { excludeTaskSections: showTasks }),
        [activeLanguage, items, showTasks],
    );
    const skillRows = useMemo(() => buildSkillComparison(items, activeLanguage), [activeLanguage, items]);
    const taskColumns = useMemo(
        () => (showTasks ? buildProfileTaskComparison(items, activeLanguage) : []),
        [activeLanguage, items, showTasks],
    );
    const openComparedTask = useOpenComparedTask();
    const taskCount = taskColumns.reduce((count, column) => count + column.tasks.length, 0);
    const { isEmpty, showLoadingRows } = getComparisonContentState({
        columns,
        sectionRowCount: sectionRows.length,
        skillRowCount: skillRows.length,
        taskCount,
    });
    const [isJobOverviewCollapsed, setIsJobOverviewCollapsed] = useState(false);
    const [isTasksCollapsed, setIsTasksCollapsed] = useState(false);
    const [isRequiredSkillsCollapsed, setIsRequiredSkillsCollapsed] = useState(false);
    const columnWidths = useMemo<ReadonlyArray<TableColumnWidth>>(
        () => [
            ...columns.map(() => `minmax(${MIN_COLUMN_WIDTH}px, 1fr)`),
            ...(is.undefined(trailingColumn) ? [] : [trailingColumn.width]),
        ],
        [columns, trailingColumn],
    );
    const trailingCell = is.undefined(trailingColumn) ? null : <TableCell aria-hidden={true} role="gridcell" />;
    const showTasksGroup = showTasks && taskCount > 0;

    const emptyState = (
        <div className="flex h-full min-h-[12rem] items-center justify-center rounded-xxl bg-surface-hover px-xxl text-center text-body-s text-text-muted">
            {emptyMessage}
        </div>
    );

    // Column headers stay visible for an empty comparison so a version can still be switched or an item
    // removed; only a comparison without columns has nothing to show but the message.
    if (columns.length === 0) {
        return emptyState;
    }

    return (
        <TableContainer>
            <TableGrid aria-label={label} className="h-full grid-rows-[auto_minmax(0,1fr)]" role="grid">
                <TableHeader $columnWidths={columnWidths} role="row">
                    {columns.map((column) => (
                        <TableHeaderCell key={column.id} className="justify-start !py-s text-left" role="columnheader">
                            {renderHeaderCell(column)}
                        </TableHeaderCell>
                    ))}
                    {is.undefined(trailingColumn) ? null : (
                        <TableHeaderCell className="!justify-center !py-s text-center" role="columnheader">
                            {trailingColumn.header}
                        </TableHeaderCell>
                    )}
                </TableHeader>
                <TableBody className="content-start">
                    {isEmpty ? (
                        <TableFullWidthRow $autoHeight={true} $columnWidths={columnWidths} role="row">
                            {emptyState}
                        </TableFullWidthRow>
                    ) : null}
                    {showLoadingRows ? (
                        <ComparisonLoadingRows
                            columns={columns}
                            columnWidths={columnWidths}
                            label={label}
                            trailingCell={trailingCell}
                        />
                    ) : null}
                    {sectionRows.length > 0 ? (
                        <ComparisonGroupHeader
                            columnWidths={columnWidths}
                            hasTopMargin={false}
                            isCollapsed={isJobOverviewCollapsed}
                            title={translate('pages.job-architecture-v2.job-profile-title')}
                            onToggle={() => setIsJobOverviewCollapsed((collapsed) => !collapsed)}
                        />
                    ) : null}
                    {!isJobOverviewCollapsed
                        ? sectionRows.map((row) => (
                              <TableRow
                                  key={row.id}
                                  $animateLayout={false}
                                  $autoHeight={true}
                                  $columnWidths={columnWidths}
                                  role="row"
                              >
                                  {columns.map((column) => (
                                      <ProfileComparisonCell
                                          key={column.id}
                                          column={column}
                                          row={row}
                                          sectionTitle={resolveSectionTitle({ id: row.sectionId, tag: row.tag })}
                                      />
                                  ))}
                                  {trailingCell}
                              </TableRow>
                          ))
                        : null}
                    {showTasksGroup ? (
                        <ComparisonGroupHeader
                            columnWidths={columnWidths}
                            hasTopMargin={sectionRows.length > 0}
                            isCollapsed={isTasksCollapsed}
                            title={translate(profileTaskTranslationKeys.title)}
                            onToggle={() => setIsTasksCollapsed((collapsed) => !collapsed)}
                        />
                    ) : null}
                    {showTasksGroup && !isTasksCollapsed ? (
                        <TableRow
                            $animateLayout={false}
                            $autoHeight={true}
                            $columnWidths={columnWidths}
                            data-testid="comparison-tasks-row"
                            role="row"
                        >
                            {columns.map((column) => (
                                <TaskComparisonCell
                                    key={column.id}
                                    column={column}
                                    taskColumn={taskColumns.find(({ itemId }) => itemId === column.id)}
                                    onOpenTask={openComparedTask}
                                />
                            ))}
                            {trailingCell}
                        </TableRow>
                    ) : null}
                    {skillRows.length > 0 ? (
                        <ComparisonGroupHeader
                            columnWidths={columnWidths}
                            hasTopMargin={sectionRows.length > 0 || showTasksGroup}
                            isCollapsed={isRequiredSkillsCollapsed}
                            title={translate('pages.job-architecture-v2.job-profile-required-skills')}
                            onToggle={() => setIsRequiredSkillsCollapsed((collapsed) => !collapsed)}
                        />
                    ) : null}
                    {!isRequiredSkillsCollapsed
                        ? skillRows.map((row, index) => {
                              const previousRow = skillRows[index - 1];
                              const isNewCompetency = startsNewCompetency(row, previousRow);

                              return (
                                  <Fragment key={row.id}>
                                      {isNewCompetency ? (
                                          <ComparisonGroupHeader
                                              columnWidths={columnWidths}
                                              hasTopMargin={index > 0 || sectionRows.length > 0 || showTasksGroup}
                                              title={row.competencyTitle ?? ''}
                                          />
                                      ) : null}
                                      <TableRow $animateLayout={false} $columnWidths={columnWidths} role="row">
                                          {columns.map((column) => (
                                              <SkillComparisonCellView
                                                  key={column.id}
                                                  column={column}
                                                  highlightProficiency={highlightProficiency}
                                                  row={row}
                                                  showProficiency={showProficiency}
                                                  onOpenSkill={onOpenSkill}
                                              />
                                          ))}
                                          {trailingCell}
                                      </TableRow>
                                  </Fragment>
                              );
                          })
                        : null}
                </TableBody>
            </TableGrid>
        </TableContainer>
    );
};
